import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, LogOut, Users, Copy, Power, KeyRound, Trash2, Loader2, CheckCircle2, XCircle, Search, Settings, Lock, Wrench, CalendarClock, SlidersHorizontal, AlertTriangle } from "lucide-react";

const MODULE_LABELS = {
  grant_finder: "Grant Finder",
  proposal_writer: "Proposal Writer",
  research_papers: "Research Papers",
  consultancy: "Consultancy",
  travel_grants: "Travel Grants",
  phd_thesis: "PhD Thesis",
  fellowship_finder: "Fellowship Finder",
};

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", title: "Prof.", institution: "", discipline: "", password: "", validity_days: 30 });
  const [showCreds, setShowCreds] = useState(null);
  const [stats, setStats] = useState(null);
  const [maintOpen, setMaintOpen] = useState(false);
  const [maint, setMaint] = useState({ maintenance_mode: false, maintenance_message: "" });
  const [permsUser, setPermsUser] = useState(null); // user currently being edited for perms
  const [permsDraft, setPermsDraft] = useState({ modules: {}, extend_days: 30 });
  const nav = useNavigate();

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); setMaint({ maintenance_mode: !!data.maintenance_mode, maintenance_message: data.maintenance_message || "" }); }
    catch {}
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data || []); }
    catch (e) {
      if (e?.response?.status === 403) { nav("/admin/login"); return; }
      toast.error("Could not load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadStats(); }, []); // eslint-disable-line

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.full_name) return toast.error("Email and Name are required");
    const pwd = (form.password || "").trim();
    if (pwd && pwd.length < 8) return toast.error("Custom password must be at least 8 characters");
    setCreating(true);
    try {
      const payload = { ...form, password: pwd || undefined, validity_days: Number(form.validity_days) || 30 };
      const { data } = await api.post("/admin/users", payload);
      setShowCreds({ email: data.user.email, password: data.one_time_password });
      setForm({ email: "", full_name: "", title: "Prof.", institution: "", discipline: "", password: "", validity_days: 30 });
      toast.success("User created — copy the credentials before closing.");
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const saveMaintenance = async () => {
    try {
      await api.post("/admin/maintenance", maint);
      toast.success(`Maintenance mode ${maint.maintenance_mode ? "ENABLED" : "disabled"}`);
      setMaintOpen(false);
      loadStats();
    } catch { toast.error("Could not update maintenance"); }
  };

  const openPerms = (u) => {
    setPermsUser(u);
    const mods = u.modules_enabled || {};
    setPermsDraft({
      modules: Object.fromEntries(Object.keys(MODULE_LABELS).map(k => [k, mods[k] !== false])),
      extend_days: 30,
    });
  };

  const savePerms = async () => {
    try {
      await api.patch(`/admin/users/${permsUser.id}`, { modules_enabled: permsDraft.modules });
      toast.success(`Module permissions updated for ${permsUser.email}`);
      setPermsUser(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const extendValidity = async () => {
    try {
      const d = Number(permsDraft.extend_days) || 30;
      await api.patch(`/admin/users/${permsUser.id}`, { extend_days: d });
      toast.success(`Extended validity by ${d} days`);
      setPermsUser(null);
      load(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Extend failed"); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A one-time password will be shown.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setShowCreds({ email: data.email, password: data.one_time_password });
    } catch { toast.error("Reset failed"); }
  };
  const toggleActive = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-active`);
      toast.success(`User ${data.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} permanently? Their proposals will remain.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const logout = () => { localStorage.removeItem("gf_token"); localStorage.removeItem("gf_role"); nav("/admin/login"); };
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  // Self-service password change
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const submitPw = async (e) => {
    e?.preventDefault?.();
    if (pw.new_password !== pw.confirm) return toast.error("New password and confirmation don't match");
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    setPwBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated — signing you out.");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "", confirm: "" });
      setTimeout(() => { localStorage.removeItem("gf_token"); localStorage.removeItem("gf_role"); nav("/admin/login"); }, 800);
    } catch (err) { toast.error(err?.response?.data?.detail || "Change failed"); }
    finally { setPwBusy(false); }
  };

  const filtered = users.filter(u => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.email||"").toLowerCase().includes(s) || (u.full_name||"").toLowerCase().includes(s) || (u.institution||"").toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen paper" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-primary">
            <ShieldCheck className="w-5 h-5"/><span className="font-display text-xl">GrantForge Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={()=>setPwOpen(true)} data-testid="admin-open-change-pw"><Settings className="w-4 h-4 mr-1"/>Change my password</Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1"/>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Macro stats row */}
        <div className="grid md:grid-cols-5 gap-3">
          <StatCard label="Total users" value={stats?.total_users ?? "—"} testid="stat-total"/>
          <StatCard label="Active" value={stats?.active_users ?? "—"} tone="success" testid="stat-active"/>
          <StatCard label="Expiring in 7 days" value={stats?.expiring_soon_7d ?? "—"} tone="warn" testid="stat-expiring"/>
          <StatCard label="Already expired" value={stats?.already_expired ?? "—"} tone="danger" testid="stat-expired"/>
          <button onClick={()=>setMaintOpen(true)} className={`text-left rounded-lg p-4 border transition-colors ${stats?.maintenance_mode ? "bg-destructive/10 border-destructive text-destructive" : "bg-card border-border hover:border-primary"}`} data-testid="stat-maintenance">
            <div className="overline flex items-center gap-1"><Wrench className="w-3.5 h-3.5"/>Maintenance</div>
            <div className="font-display text-2xl mt-1">{stats?.maintenance_mode ? "ON" : "Off"}</div>
            <div className="text-xs text-muted-foreground mt-1">Click to toggle</div>
          </button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline">Control panel</div>
            <h1 className="font-display text-4xl mt-1 flex items-center gap-2"><Users className="w-8 h-8 text-primary"/>User accounts</h1>
            <p className="text-muted-foreground text-sm mt-1">Provision new professor accounts, control which modules each professor sees, set validity, reset passwords. Public registration is closed — every user comes through you.</p>
          </div>
        </div>

        {/* Create user card */}
        <form className="card-crisp rounded-lg p-5" onSubmit={create} data-testid="admin-create-form">
          <div className="font-display text-2xl mb-3 flex items-center gap-2"><Plus className="w-5 h-5 text-primary"/>Provision a new user</div>
          <div className="grid md:grid-cols-7 gap-3">
            <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="admin-new-email"/></div>
            <div><Label>Full name *</Label><Input required value={form.full_name} onChange={(e)=>setForm({...form, full_name:e.target.value})} data-testid="admin-new-name"/></div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="admin-new-title"/></div>
            <div><Label>Institution</Label><Input value={form.institution} onChange={(e)=>setForm({...form, institution:e.target.value})} data-testid="admin-new-inst"/></div>
            <div><Label>Discipline</Label><Input value={form.discipline} onChange={(e)=>setForm({...form, discipline:e.target.value})} data-testid="admin-new-disc"/></div>
            <div>
              <Label>Password (optional)</Label>
              <Input type="text" placeholder="Auto-generate" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="admin-new-password" autoComplete="off"/>
            </div>
            <div>
              <Label>Validity (days)</Label>
              <Input type="number" min="1" value={form.validity_days} onChange={(e)=>setForm({...form, validity_days:e.target.value})} data-testid="admin-new-validity"/>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">Default validity is 30 days from creation. On expiry the account auto-disables. Safe-charset auto-gen avoids 0/O/1/l/I ambiguity.</div>
            <Button type="submit" disabled={creating} data-testid="admin-create-btn">{creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Plus className="w-4 h-4 mr-2"/>}Create user</Button>
          </div>
        </form>

        {/* Users list */}
        <div className="card-crisp rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-2xl">All users</div>
            <div className="flex items-center gap-2 border border-border rounded-full px-3 py-1 bg-background">
              <Search className="w-4 h-4 text-muted-foreground"/>
              <input placeholder="Search email / name / institution" value={q} onChange={(e)=>setQ(e.target.value)} className="bg-transparent text-sm outline-none w-64" data-testid="admin-search"/>
            </div>
          </div>
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b border-border">
                  <tr><th className="py-2">User</th><th>Institution</th><th>Discipline</th><th>Expires</th><th>Proposals</th><th>Role</th><th>Status</th><th className="text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map(u => {
                    const exp = u.expires_at ? new Date(u.expires_at) : null;
                    const daysLeft = exp ? Math.ceil((exp.getTime() - Date.now()) / (1000*60*60*24)) : null;
                    return (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                      <td className="py-2"><div className="font-medium">{u.full_name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                      <td className="text-xs">{u.institution || "—"}</td>
                      <td className="text-xs">{u.discipline || "—"}</td>
                      <td className={`text-xs ${daysLeft !== null && daysLeft <= 7 ? "text-red-700 font-medium" : ""}`}>
                        {u.role === "admin" ? "—" : (exp ? `${exp.toLocaleDateString()} · ${daysLeft}d` : "—")}
                      </td>
                      <td>{u.proposals ?? 0}</td>
                      <td><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary"}`}>{u.role || "user"}</span></td>
                      <td>{u.is_active === false ? <span className="text-xs text-red-700 inline-flex items-center gap-1"><XCircle className="w-3 h-3"/>Inactive</span> : <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>Active</span>}</td>
                      <td className="text-right space-x-1">
                        {u.role !== "admin" && <Button size="sm" variant="ghost" onClick={()=>openPerms(u)} title="Modules & validity" data-testid={`admin-perms-${u.id}`}><SlidersHorizontal className="w-4 h-4"/></Button>}
                        <Button size="sm" variant="ghost" onClick={()=>resetPw(u)} title="Reset password" data-testid={`admin-reset-${u.id}`}><KeyRound className="w-4 h-4"/></Button>
                        {u.role !== "admin" && <Button size="sm" variant="ghost" onClick={()=>toggleActive(u)} title="Toggle active" data-testid={`admin-toggle-${u.id}`}><Power className={`w-4 h-4 ${u.is_active === false ? "text-red-600" : ""}`}/></Button>}
                        {u.role !== "admin" && <Button size="sm" variant="ghost" onClick={()=>removeUser(u)} title="Delete" data-testid={`admin-delete-${u.id}`}><Trash2 className="w-4 h-4 text-destructive"/></Button>}
                      </td>
                    </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No users match.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* One-time credentials modal */}
      <Dialog open={!!showCreds} onOpenChange={(o)=>{ if (!o) setShowCreds(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><KeyRound className="w-6 h-6 text-primary"/>One-time credentials</DialogTitle></DialogHeader>
          {showCreds && (
            <div className="space-y-3" data-testid="admin-creds-modal">
              <p className="text-sm text-muted-foreground">Copy these now and email them to the user. They will not be shown again.</p>
              <div className="rounded-md border border-border p-3">
                <div className="text-xs text-muted-foreground">Email</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm">{showCreds.email}</code>
                  <Button size="sm" variant="ghost" onClick={()=>copy(showCreds.email)}><Copy className="w-3.5 h-3.5"/></Button>
                </div>
              </div>
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
                <div className="text-xs text-primary">One-time password</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-lg font-mono" data-testid="admin-creds-password">{showCreds.password}</code>
                  <Button size="sm" variant="outline" onClick={()=>copy(showCreds.password)}><Copy className="w-3.5 h-3.5 mr-1"/>Copy</Button>
                </div>
              </div>
              <Button size="sm" variant="outline" className="w-full" onClick={()=>copy(`Email: ${showCreds.email}\nPassword: ${showCreds.password}`)}><Copy className="w-4 h-4 mr-2"/>Copy both</Button>
            </div>
          )}
          <DialogFooter><Button onClick={()=>setShowCreds(null)} data-testid="admin-creds-close">I've saved these</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Self-service password change */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Lock className="w-6 h-6 text-primary"/>Change my admin password</DialogTitle></DialogHeader>
          <form onSubmit={submitPw} className="space-y-3" data-testid="admin-change-pw-form">
            <div>
              <Label>Current password</Label>
              <Input type="password" required autoComplete="current-password" value={pw.current_password}
                onChange={(e)=>setPw({...pw, current_password: e.target.value})} data-testid="admin-cpw-current"/>
            </div>
            <div>
              <Label>New password <span className="text-xs text-muted-foreground">(min 8 chars)</span></Label>
              <Input type="password" required autoComplete="new-password" value={pw.new_password}
                onChange={(e)=>setPw({...pw, new_password: e.target.value})} data-testid="admin-cpw-new"/>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" required autoComplete="new-password" value={pw.confirm}
                onChange={(e)=>setPw({...pw, confirm: e.target.value})} data-testid="admin-cpw-confirm"/>
            </div>
            <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-2 text-xs">
              You will be signed out and asked to sign in again with the new password.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setPwOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={pwBusy} data-testid="admin-cpw-submit">
                {pwBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Lock className="w-4 h-4 mr-2"/>}Update password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Maintenance mode dialog */}
      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Wrench className="w-6 h-6 text-primary"/>Maintenance mode</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="font-medium">Block all non-admin access</div>
                <div className="text-xs text-muted-foreground">Every professor sees a maintenance banner. Admins still have full access.</div>
              </div>
              <Switch checked={!!maint.maintenance_mode} onCheckedChange={(v)=>setMaint({...maint, maintenance_mode: v})} data-testid="maint-toggle"/>
            </div>
            <div>
              <Label>Banner message</Label>
              <Input placeholder="Back in ~15 min for a security patch." value={maint.maintenance_message} onChange={(e)=>setMaint({...maint, maintenance_message: e.target.value})} data-testid="maint-msg"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setMaintOpen(false)}>Cancel</Button>
            <Button onClick={saveMaintenance} data-testid="maint-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-user permissions dialog */}
      <Dialog open={!!permsUser} onOpenChange={(o)=>{ if (!o) setPermsUser(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><SlidersHorizontal className="w-6 h-6 text-primary"/>Access & modules — {permsUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="overline mb-1">Module access</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MODULE_LABELS).map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between rounded-md border border-border p-2">
                    <span className="text-sm">{label}</span>
                    <Switch checked={!!permsDraft.modules[k]} onCheckedChange={(v)=>setPermsDraft(d=>({...d, modules: { ...d.modules, [k]: v }}))} data-testid={`perm-${k}`}/>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-border pt-3">
              <div className="overline mb-1 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5"/>Extend validity</div>
              <div className="flex items-center gap-2">
                <Input type="number" min="1" value={permsDraft.extend_days} onChange={(e)=>setPermsDraft(d=>({...d, extend_days: e.target.value}))} className="w-28" data-testid="perm-extend-days"/>
                <span className="text-xs text-muted-foreground">days from current expiry</span>
                <Button size="sm" variant="outline" onClick={extendValidity} data-testid="perm-extend-btn"><CalendarClock className="w-3.5 h-3.5 mr-1"/>Extend</Button>
              </div>
              {permsUser?.expires_at && <div className="text-xs text-muted-foreground mt-1">Current expiry: {new Date(permsUser.expires_at).toLocaleString()}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPermsUser(null)}>Cancel</Button>
            <Button onClick={savePerms} data-testid="perm-save">Save permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = tone === "success" ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : tone === "warn"    ? "border-amber-300 bg-amber-50 text-amber-900"
                : tone === "danger"  ? "border-red-300 bg-red-50 text-red-900"
                : "border-border bg-card";
  return (
    <div className={`rounded-lg p-4 border ${toneCls}`} data-testid={testid}>
      <div className="overline opacity-80">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
